import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Modal, Button, message, Spin, Alert, Table } from 'antd';
import {
  CloudUploadOutlined,
  DownloadOutlined,
  ImportOutlined,
  LoadingOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import {
  buildProductImportTemplateXlsx,
  PRODUCT_IMPORT_COLUMNS,
  PRODUCT_IMPORT_TEMPLATE_FILENAME,
} from './productImportTemplate';
import { createDemoProductImportFile, PRODUCT_IMPORT_DEMO_FAILURES } from './productImportDemo';
import {
  buildProductImportTestCaseXlsx,
  PRODUCT_IMPORT_TEST_CASES,
  PRODUCT_IMPORT_TEST_CASE_SECTION,
} from './productImportTestCases';
import './ProductImportModal.css';

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const IMPORT_POLL_MS = 3000;
const IMPORT_POLL_MAX_MS = 5 * 60 * 1000;
const SHOW_BULK_IMPORT_TEST_TOOLS = process.env.REACT_APP_TEST_BULK_IMPORT_FILE === 'true';

function downloadBuffer(buffer, filename) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const blob = new Blob([bytes], { type: XLSX_CONTENT_TYPE });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const FAIL_REPORT_COLUMNS = [
  { title: 'Row', dataIndex: 'row', key: 'row', width: 72, align: 'center' },
  { title: 'Type', dataIndex: 'type', key: 'type', width: 96 },
  { title: 'Field', dataIndex: 'field', key: 'field', width: 160 },
  { title: 'Issue', dataIndex: 'issue', key: 'issue' },
];

function getFileExtension(filename = '') {
  const match = filename.toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
}

function isAcceptedFile(file) {
  return ACCEPTED_EXTENSIONS.includes(getFileExtension(file?.name));
}

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildImportFormData(file, username) {
  const formData = new FormData();
  formData.append('file', file);
  if (username) {
    formData.append('username', username);
  }
  return formData;
}

function toFailRows(errors, fallbackIssue) {
  if (Array.isArray(errors) && errors.length) {
    return errors.map((err, index) => ({
      key: `${err.row ?? index}-${err.field ?? 'ไฟล์'}-${index}`,
      row: err.row ?? 1,
      type: err.type || 'รูปแบบ',
      field: err.field || 'ไฟล์',
      issue: err.issue || fallbackIssue || 'นำเข้าไม่สำเร็จ',
    }));
  }
  return [
    {
      key: 'file-0',
      row: 1,
      type: 'รูปแบบ',
      field: 'ไฟล์',
      issue: fallbackIssue || 'นำเข้าไม่สำเร็จ',
    },
  ];
}

async function downloadTemplate(categories) {
  if (!categories?.length) {
    message.warning('ไม่พบหมวดหมู่สินค้าในระบบ กรุณากรอกชื่อหมวดหมู่ให้ตรงกับที่มีอยู่');
  }

  const buffer = await buildProductImportTemplateXlsx(categories);
  downloadBuffer(buffer, PRODUCT_IMPORT_TEMPLATE_FILENAME);
}

async function pollImportJob({ appHostname, token, jobId, cancelledRef }) {
  const started = Date.now();
  while (!cancelledRef.current) {
    const { data } = await axios.get(`${appHostname}/api/product/item/import/${jobId}`, {
      headers: authHeaders(token),
    });
    if (data?.job_status === 'COMPLETED' || data?.job_status === 'FAILED') {
      return data;
    }
    if (data?.status !== 'OK') {
      return {
        job_status: 'FAILED',
        errors: data?.errors,
        message: data?.message || 'อ่านสถานะงานนำเข้าไม่สำเร็จ',
      };
    }
    if (Date.now() - started >= IMPORT_POLL_MAX_MS) {
      return {
        job_status: 'FAILED',
        errors: [{ row: 1, type: 'รูปแบบ', field: 'ไฟล์', issue: 'หมดเวลารอผลการนำเข้า' }],
      };
    }
    await new Promise((resolve) => setTimeout(resolve, IMPORT_POLL_MS));
  }
  return { job_status: 'FAILED', errors: [] };
}

function ProductImportModal({
  isOpen,
  onClose,
  onFileSelected,
  onImported,
  appHostname,
  token,
  username,
  categories,
  existingProducts,
}) {
  const fileInputRef = useRef(null);
  const cancelledRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [step, setStep] = useState('upload');
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [downloadingCaseId, setDownloadingCaseId] = useState('');
  const [apiFailures, setApiFailures] = useState([]);
  const [isDemoFlow, setIsDemoFlow] = useState(false);
  const failRows = apiFailures.length ? apiFailures : PRODUCT_IMPORT_DEMO_FAILURES;

  useEffect(() => {
    cancelledRef.current = !isOpen;
  }, [isOpen]);

  const resetState = () => {
    setIsDragging(false);
    setSelectedFile(null);
    setStep('upload');
    setApiFailures([]);
    setIsDemoFlow(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDemoSuccess = () => {
    handleClose();
    message.success('นำเข้าไฟล์สินค้าสำเร็จ');
  };

  const handleDemoFail = () => {
    setApiFailures([]);
    setStep('fail');
  };

  const handleTest = () => {
    const demoFile = createDemoProductImportFile();
    acceptFile(demoFile);
    setIsDemoFlow(true);
    setStep('processing');
  };

  const handleClose = () => {
    cancelledRef.current = true;
    resetState();
    onClose();
  };

  const showFail = (errors, fallbackIssue) => {
    setApiFailures(toFailRows(errors, fallbackIssue));
    setStep('fail');
  };

  const acceptFile = (file) => {
    if (!file) return;

    if (!isAcceptedFile(file)) {
      message.error('รองรับเฉพาะไฟล์นามสกุล .csv, .xlsx, .xls เท่านั้น');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error('ขนาดไฟล์สูงสุด 10MB');
      return;
    }

    setSelectedFile(file);
    if (onFileSelected) {
      onFileSelected(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer?.files?.[0];
    acceptFile(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    acceptFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      message.error('กรุณาเลือกไฟล์');
      return;
    }
    if (!appHostname) {
      message.error('ไม่พบที่อยู่ API');
      return;
    }

    cancelledRef.current = false;
    setApiFailures([]);
    setIsDemoFlow(false);
    setStep('processing');

    try {
      const validateResp = await axios.post(
        `${appHostname}/api/product/item/import/validate`,
        buildImportFormData(selectedFile, username),
        { headers: authHeaders(token) },
      );
      if (cancelledRef.current) return;
      const validateData = validateResp.data || {};
      if (validateData.status !== 'OK') {
        showFail(validateData.errors, validateData.message);
        return;
      }

      const importResp = await axios.post(
        `${appHostname}/api/product/item/import`,
        buildImportFormData(selectedFile, username),
        { headers: authHeaders(token) },
      );
      if (cancelledRef.current) return;
      const importData = importResp.data || {};
      if (importData.status !== 'OK' || !importData.job_id) {
        showFail(importData.errors, importData.message || 'สร้างงานนำเข้าไม่สำเร็จ');
        return;
      }

      const job = await pollImportJob({
        appHostname,
        token,
        jobId: importData.job_id,
        cancelledRef,
      });
      if (cancelledRef.current) return;
      if (job.job_status === 'COMPLETED') {
        if (onImported) {
          onImported();
        }
        handleClose();
        message.success('นำเข้าไฟล์สินค้าสำเร็จ');
        return;
      }
      showFail(job.errors, job.message || 'นำเข้าไม่สำเร็จ');
    } catch (error) {
      if (cancelledRef.current) return;
      const data = error.response?.data;
      showFail(data?.errors, data?.message || error.message || 'นำเข้าไม่สำเร็จ');
    }
  };

  return (
    <Modal
      className='product-import-modal'
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      destroyOnClose
      title={
        <div className='product-import-modal-header'>
          <div className='product-import-modal-title-row'>
            <ImportOutlined className='product-import-modal-title-icon' />
            <span>นำเข้าข้อมูลสินค้า</span>
          </div>
          <div className='product-import-modal-subtitle'>
            อัปโหลดไฟล์ข้อมูลสินค้าเพื่อเพิ่มรายการสินค้าใหม่หรืออัปเดตสต็อกในระบบของคุณ
          </div>
        </div>
      }
    >
      <input
        ref={fileInputRef}
        type='file'
        accept='.csv,.xlsx,.xls'
        className='product-import-modal-file-input'
        onChange={handleFileChange}
        data-testid='product-import-file-input'
      />

      {step === 'fail' && (
        <div className='product-import-fail-report' data-testid='product-import-fail-report'>
          <Alert
            className='product-import-fail-alert'
            type='error'
            showIcon
            icon={<ExclamationCircleFilled />}
            message={`พบข้อมูลไม่ผ่านการตรวจสอบ ${failRows.length} รายการ`}
            description='กรุณาแก้ไขให้ครบทุกรายการก่อนนำเข้าข้อมูล ระบบยังไม่บันทึกแถวใดจนกว่าจะแก้ไขครบ'
          />
          <Table
            className='product-import-fail-table'
            columns={FAIL_REPORT_COLUMNS}
            dataSource={failRows}
            rowKey='key'
            pagination={false}
            scroll={{ y: 280 }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? 'product-import-fail-row-odd' : 'product-import-fail-row-even'
            }
          />
          <div className='product-import-fail-actions'>
            <Button className='product-import-retry-btn' onClick={resetState}>
              อัปโหลดใหม่
            </Button>
          </div>
        </div>
      )}

      {step === 'processing' && (
        <>
          <div
            className='product-import-dropzone is-processing'
            data-testid='product-import-processing'
          >
            <Spin
              indicator={<LoadingOutlined className='product-import-processing-spinner' spin />}
            />
            <div className='product-import-dropzone-copy'>
              <div className='product-import-dropzone-title'>กำลังประมวลผลไฟล์ของคุณ...</div>
              <div className='product-import-dropzone-hint'>กรุณารอสักครู่</div>
            </div>
          </div>
          {SHOW_BULK_IMPORT_TEST_TOOLS && isDemoFlow && (
            <div className='product-import-processing-actions'>
              <Button className='product-import-success-btn' onClick={handleDemoSuccess}>
                Success
              </Button>
              <Button className='product-import-fail-btn' onClick={handleDemoFail}>
                Fail
              </Button>
            </div>
          )}
        </>
      )}

      {step === 'upload' && (
        <>
          <div
            className={`product-import-dropzone${isDragging ? ' is-dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            role='button'
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <CloudUploadOutlined className='product-import-dropzone-icon' />
            <div className='product-import-dropzone-copy'>
              <div className='product-import-dropzone-title'>
                {selectedFile
                  ? selectedFile.name
                  : 'ลากและวางไฟล์ของคุณที่นี่ หรือคลิกเพื่อค้นหาไฟล์'}
              </div>
              <div className='product-import-dropzone-hint'>
                รองรับเฉพาะไฟล์นามสกุล .csv, .xlsx, .xls เท่านั้น
                <br />
                (ขนาดสูงสุด 10MB หรือไม่เกิน 5,000 แถว)
              </div>
            </div>
            <Button
              type='primary'
              className='product-import-select-btn'
              onClick={(event) => {
                event.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              เลือกไฟล์จากเครื่องคอมพิวเตอร์
            </Button>
          </div>
          <div className='product-import-requirements'>
            <div className='product-import-requirements-title'>ข้อกำหนดคอลัมน์ในไฟล์ที่จำเป็น:</div>
            <div className='product-import-requirements-list'>
              {PRODUCT_IMPORT_COLUMNS.map((column) => (
                <div key={column.header} className='product-import-requirement-row'>
                  <span className='product-import-requirement-label'>• {column.header}:</span>
                  <span className='product-import-requirement-desc'>{column.description}</span>
                </div>
              ))}
            </div>
            <div className='product-import-requirement-actions'>
              <Button
                className='product-import-template-btn'
                icon={<DownloadOutlined />}
                loading={isDownloadingTemplate}
                onClick={async (event) => {
                  event.stopPropagation();
                  setIsDownloadingTemplate(true);
                  try {
                    await downloadTemplate(categories);
                  } catch {
                    message.error('สร้างไฟล์ตัวอย่างไม่สำเร็จ');
                  } finally {
                    setIsDownloadingTemplate(false);
                  }
                }}
              >
                ดาวน์โหลดไฟล์ตัวอย่าง
              </Button>
              {SHOW_BULK_IMPORT_TEST_TOOLS && (
                <Button
                  className='product-import-test-btn'
                  onClick={(event) => {
                    event.stopPropagation();
                    handleTest();
                  }}
                >
                  Test
                </Button>
              )}
              <Button
                type='primary'
                className='product-import-run-btn'
                icon={<ImportOutlined />}
                disabled={!selectedFile}
                onClick={(event) => {
                  event.stopPropagation();
                  handleImport();
                }}
              >
                นำเข้า
              </Button>
            </div>
          </div>
          {SHOW_BULK_IMPORT_TEST_TOOLS && (
            <div className='product-import-test-cases' data-testid='product-import-test-cases'>
              <div className='product-import-test-cases-title'>
                {PRODUCT_IMPORT_TEST_CASE_SECTION}
              </div>
              <div className='product-import-test-cases-hint'>
                ไฟล์ตัวอย่างสำหรับทดสอบ bulk import — ลบ section นี้ได้เมื่อเลิกใช้
              </div>
              <div className='product-import-test-cases-list'>
                {PRODUCT_IMPORT_TEST_CASES.map((testCase) => (
                  <div key={testCase.id} className='product-import-test-case-row'>
                    <div className='product-import-test-case-copy'>
                      <div className='product-import-test-case-label'>{testCase.label}</div>
                      <div className='product-import-test-case-desc'>{testCase.description}</div>
                      <div className='product-import-test-case-expected'>
                        คาดหวัง: {testCase.expected}
                      </div>
                    </div>
                    <Button
                      size='small'
                      icon={<DownloadOutlined />}
                      loading={downloadingCaseId === testCase.id}
                      data-testid={`product-import-tc-${testCase.id}`}
                      onClick={async (event) => {
                        event.stopPropagation();
                        setDownloadingCaseId(testCase.id);
                        try {
                          const { filename, buffer } = buildProductImportTestCaseXlsx(testCase, {
                            categories,
                            existingProducts,
                          });
                          downloadBuffer(buffer, filename);
                        } catch (error) {
                          message.warning(error.message || 'สร้างไฟล์ทดสอบไม่สำเร็จ');
                        } finally {
                          setDownloadingCaseId('');
                        }
                      }}
                    >
                      ดาวน์โหลด
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

ProductImportModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onFileSelected: PropTypes.func,
  onImported: PropTypes.func,
  appHostname: PropTypes.string,
  token: PropTypes.string,
  username: PropTypes.string,
  existingProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productitem_id: PropTypes.string,
      productitem_name: PropTypes.string,
      productitem_inter: PropTypes.string,
    }),
  ),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      productcategory_id: PropTypes.string,
      productcategory_name: PropTypes.string,
      productcategory_sortno: PropTypes.number,
    }),
  ),
};

export default ProductImportModal;
